import React from 'react';

/**
 * Live HTML preview of the Consultation Summary — mirrors the layout of
 * `consultation_summary_pdf_service.py` so admin sees changes as they type.
 * Not a pixel-perfect PDF render; a faithful, on-brand preview.
 */

const fmtDate = (iso) => {
  if (!iso) return '';
  const s = String(iso).trim();
  if (!s || s.toUpperCase() === 'TBD') return s ? 'TBD' : '';
  // Parse YYYY-MM-DD as a *local* date so the day never shifts backwards
  // (Cayman is GMT-5 — parsing as UTC would drop a day).
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  const d = m ? new Date(+m[1], +m[2] - 1, +m[3]) : new Date(s);
  if (isNaN(d.getTime())) return s;
  return d.toLocaleDateString('en-US', { day: '2-digit', month: 'long', year: 'numeric' });
};

// Compact date used inside the "next steps" chip so it doesn't run off the row.
const fmtDateShort = (iso) => {
  if (!iso) return '';
  const s = String(iso).trim();
  if (!s || s.toUpperCase() === 'TBD') return s ? 'TBD' : '';
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  const d = m ? new Date(+m[1], +m[2] - 1, +m[3]) : new Date(s);
  if (isNaN(d.getTime())) return s;
  return d.toLocaleDateString('en-US', { day: '2-digit', month: 'short', year: 'numeric' });
};

const Tile = ({ label, value }) => (
  <div className="bg-teal-100/70 rounded-lg px-3 py-2">
    <div className="text-[9px] font-semibold uppercase tracking-wider text-teal-800">{label}</div>
    <div className="text-[11px] font-bold text-slate-900 leading-tight mt-0.5 line-clamp-2">{value || '—'}</div>
  </div>
);

const RouteStrip = ({ origin, destination, stops }) => {
  const nodes = [
    { title: origin || 'Origin', endpoint: true },
    ...stops.map((s) => ({
      title: s.location,
      flight: [s.carrier, s.flight_number].filter(Boolean).join(' ').trim(),
      dwell: s.dwell || '',
    })),
    { title: destination || 'Destination', endpoint: true },
  ];
  return (
    <div className="bg-teal-100/70 rounded-lg px-3 py-2">
      <div className="text-[9px] font-semibold uppercase tracking-wider text-teal-800 mb-2">Route (with transits)</div>
      <div className="flex items-stretch gap-1">
        {nodes.map((n, i) => (
          <React.Fragment key={i}>
            <div
              className={`flex-1 min-w-0 rounded-md px-1.5 py-1 text-white ${n.endpoint ? 'bg-teal-700' : 'bg-purple-400'}`}
            >
              <div className="text-[10px] font-bold truncate leading-tight">{n.title}</div>
              {!n.endpoint && n.flight && <div className="text-[8.5px] font-semibold truncate">{n.flight}</div>}
              {!n.endpoint && n.dwell && <div className="text-[8px] opacity-90 truncate">{n.dwell}</div>}
            </div>
            {i < nodes.length - 1 && (
              <div className="flex items-center text-teal-700 text-[10px]">→</div>
            )}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
};

export const ConsultationSummaryPreview = ({ form }) => {
  const stops = (form.transit_stops || []).filter((s) => (s.location || '').trim());
  const dps  = (form.discussion_points || []).filter((p) => (p || '').trim());
  const ns   = (form.next_steps || []).filter((s) => (s.action || '').trim());
  const tl   = (form.timeline_items || []).filter((t) => (t.milestone || t.when || '').trim());

  // Closest-upcoming milestone (has when_date >= today and smallest delta).
  const nextIdx = React.useMemo(() => {
    const today = new Date(); today.setHours(0, 0, 0, 0);
    let bestIdx = -1, bestDelta = Infinity;
    tl.forEach((t, i) => {
      const s = String(t.when_date || '').trim();
      const m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
      if (!m) return;
      const d = new Date(+m[1], +m[2] - 1, +m[3]);
      const delta = (d - today) / 86_400_000;
      if (delta >= 0 && delta < bestDelta) { bestDelta = delta; bestIdx = i; }
    });
    return bestIdx;
  }, [tl]);

  return (
    <div className="bg-white rounded overflow-hidden text-slate-900" data-testid="summary-html-preview">
      {/* Hero band */}
      <div className="bg-teal-700 text-white px-4 py-3 relative overflow-hidden">
        <div className="absolute -right-4 -top-4 w-16 h-16 rounded-full bg-white/10" />
        <div className="text-[9px] font-semibold tracking-wider">PAWSOME VENTURES</div>
        <div className="italic text-[9px] opacity-90">Making stress-free pet travel pawsible</div>
        <div className="text-lg font-bold mt-1">Consultation Summary</div>
      </div>

      <div className="p-3 space-y-2">
        {/* Meta row 1 */}
        <div className="grid grid-cols-2 gap-1.5">
          <Tile label="Client" value={form.client_name} />
          <Tile label="Consultation date" value={fmtDate(form.consultation_date)} />
        </div>

        {/* Route strip OR 3-tile row */}
        {stops.length > 0 ? (
          <RouteStrip origin={form.origin} destination={form.destination} stops={stops} />
        ) : (
          <div className="grid grid-cols-3 gap-1.5">
            <Tile label="Origin" value={form.origin} />
            <Tile label="Destination" value={form.destination} />
            <Tile label="In transit via" value={form.transit_via || 'N/A'} />
          </div>
        )}

        {/* Pets */}
        <Tile label="Pet(s)" value={form.pet_names} />

        {form.attendees && (
          <div className="text-[10px] italic text-slate-500 pt-1 pb-1" data-testid="summary-preview-attendees">Attendees: {form.attendees}</div>
        )}

        {form.intro && (
          <div className="pt-2" data-testid="summary-preview-conversation">
            <div className="text-[11px] font-bold text-teal-800 border-b border-teal-500 pb-0.5 inline-block">Summary of our conversation</div>
            <p className="text-[10px] mt-2 leading-snug whitespace-pre-wrap">{form.intro}</p>
          </div>
        )}

        {dps.length > 0 && (
          <div className="pt-3" data-testid="summary-preview-discussion">
            <div className="text-[11px] font-bold text-teal-800 border-b border-teal-500 pb-0.5 inline-block">What we discussed</div>
            <ul className="mt-2 space-y-0.5">
              {dps.map((p, i) => (
                <li key={i} className="text-[10px] flex gap-1.5 items-start">
                  <span className="w-1.5 h-1.5 rounded-full bg-teal-600 mt-1 shrink-0" />
                  <span className="whitespace-pre-wrap break-words" data-testid={`summary-preview-discussion-${i}`}>{p}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {ns.length > 0 && (
          <div className="pt-3" data-testid="summary-preview-next-steps">
            <div className="text-[11px] font-bold text-teal-800 border-b border-teal-500 pb-0.5 inline-block">Agreed next steps</div>
            <ul className="mt-2 space-y-1">
              {ns.map((s, i) => (
                <li key={i} className="flex gap-2 items-start">
                  <span className="w-1.5 h-1.5 rounded-full bg-purple-400 mt-1 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="text-[10px] font-semibold whitespace-pre-wrap break-words" data-testid={`summary-preview-action-${i}`}>{s.action}</div>
                    {(s.owner || s.due_date) && (
                      <div className="inline-block bg-teal-100 rounded px-1.5 py-0.5 text-[8.5px] font-semibold text-teal-800 mt-0.5">
                        {[s.owner, s.due_date && (String(s.due_date).toUpperCase() === 'TBD' ? 'Due: TBD' : `Due: ${fmtDateShort(s.due_date)}`)].filter(Boolean).join(' · ')}
                      </div>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}

        {tl.length > 0 && (
          <div className="pt-3" data-testid="summary-preview-timeline">
            <div className="text-[11px] font-bold text-teal-800 border-b border-teal-500 pb-0.5 inline-block">Timeline &amp; milestones</div>
            <ul className="mt-2 space-y-1">
              {tl.map((t, i) => {
                const isNext = i === nextIdx;
                return (
                  <li key={i} className="flex gap-2 items-center">
                    <span className={`text-[8.5px] font-bold text-white rounded px-1.5 py-0.5 min-w-[70px] text-center truncate ${isNext ? 'bg-teal-600' : 'bg-purple-400'}`}>{t.when || '—'}</span>
                    <span className={`text-[10px] min-w-0 flex-1 whitespace-pre-wrap break-words ${isNext ? 'font-bold text-teal-800' : ''}`} data-testid={`summary-preview-milestone-${i}`}>{t.milestone}</span>
                    {isNext && (
                      <span className="text-[8px] font-bold text-white bg-amber-500 rounded px-1.5 py-0.5 shrink-0" data-testid={`tl-next-badge-${i}`}>NEXT UP</span>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        )}

        {form.notes && (
          <div className="bg-orange-50 rounded p-2">
            <div className="text-[10px] font-bold text-teal-800 mb-0.5">Notes &amp; special considerations</div>
            <p className="text-[9.5px] leading-snug whitespace-pre-wrap">{form.notes}</p>
          </div>
        )}

        <p className="text-[9px] italic text-slate-500 pt-3 whitespace-pre-wrap break-words" data-testid="summary-preview-closing">
          {form.closing_note || 'Thank you for taking the time to speak with us. If any part of this summary needs correcting, or if you think of another question, just reply to the email this arrived with — we\'re here to help.'}
        </p>

        <div className="border-t border-slate-200 pt-1 flex justify-between text-[8px] text-slate-500">
          <span>pawsomeventures.ky · pawsome.ky@gmail.com</span>
          <span>PAWSOME VENTURES</span>
        </div>
      </div>
    </div>
  );
};

export default ConsultationSummaryPreview;
